import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Calendar, Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const REQ_DATA = [
  { date: "Feb 1", random: 38000, vrf: 12000 }, { date: "Feb 3", random: 42000, vrf: 14000 },
  { date: "Feb 5", random: 35000, vrf: 11000 }, { date: "Feb 7", random: 51000, vrf: 18000 },
  { date: "Feb 9", random: 48000, vrf: 16000 }, { date: "Feb 11", random: 62000, vrf: 22000 },
  { date: "Feb 13", random: 55000, vrf: 19000 },
];

const LATENCY_DATA = [
  { range: "0-1ms", count: 12400 }, { range: "1-2ms", count: 45600 }, { range: "2-3ms", count: 38200 },
  { range: "3-4ms", count: 8900 }, { range: "4-5ms", count: 2100 }, { range: "5ms+", count: 400 },
];

const BANDWIDTH_DATA = [
  { date: "Feb 1", gb: 4.2 }, { date: "Feb 3", gb: 5.1 }, { date: "Feb 5", gb: 3.8 },
  { date: "Feb 7", gb: 6.4 }, { date: "Feb 9", gb: 5.9 }, { date: "Feb 11", gb: 7.8 }, { date: "Feb 13", gb: 6.5 },
];

const TYPE_DATA = [
  { name: "Raw QRNG", value: 35, color: "#06B6D4" },
  { name: "DRBG Expanded", value: 52, color: "#0F172A" },
  { name: "VRF Output", value: 13, color: "#64748B" },
];

const TOP_ENDPOINTS = [
  { endpoint: "/v1/random/bytes", calls: "623,412", bandwidth: "24.1 GB", avgLatency: "2.1ms", p99: "4.2ms" },
  { endpoint: "/v1/random/range", calls: "312,008", bandwidth: "8.3 GB", avgLatency: "1.8ms", p99: "3.5ms" },
  { endpoint: "/v1/vrf/generate", calls: "189,234", bandwidth: "6.2 GB", avgLatency: "3.9ms", p99: "7.1ms" },
  { endpoint: "/v1/random/stream", calls: "78,456", bandwidth: "9.6 GB", avgLatency: "1.2ms", p99: "2.8ms" },
  { endpoint: "/v1/health/status", calls: "44,722", bandwidth: "0.02 GB", avgLatency: "0.8ms", p99: "1.5ms" },
];

export default function Analytics() {
  return (
    <div data-testid="analytics-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="analytics-title">Usage Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">Detailed metrics for your quantum entropy API consumption</p>
        </div>
        <div className="flex items-center gap-3">
          <Select defaultValue="7d">
            <SelectTrigger className="w-40 h-9 text-xs" data-testid="analytics-period-select">
              <Calendar className="w-3.5 h-3.5 mr-2 text-slate-400" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Last 24 hours</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <button className="inline-flex items-center gap-2 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors text-xs px-4 py-2" data-testid="analytics-export-btn">
            <Download className="w-3.5 h-3.5" /> Export
          </button>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 p-6" data-testid="requests-chart">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Request Volume</h3>
          <p className="text-xs text-slate-500 mb-4">API calls by service type</p>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={REQ_DATA}>
              <defs>
                <linearGradient id="randomGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#06B6D4" stopOpacity={0.15} /><stop offset="95%" stopColor="#06B6D4" stopOpacity={0} /></linearGradient>
                <linearGradient id="vrfGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0F172A" stopOpacity={0.15} /><stop offset="95%" stopColor="#0F172A" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 0, fontSize: 12 }} />
              <Area type="monotone" dataKey="random" stroke="#06B6D4" strokeWidth={2} fill="url(#randomGrad)" name="Random API" />
              <Area type="monotone" dataKey="vrf" stroke="#0F172A" strokeWidth={2} fill="url(#vrfGrad)" name="VRF API" />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex gap-6 mt-3">
            <div className="flex items-center gap-2 text-xs text-slate-500"><div className="w-3 h-0.5 bg-cyan-500" />Random API</div>
            <div className="flex items-center gap-2 text-xs text-slate-500"><div className="w-3 h-0.5 bg-slate-900" />VRF API</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-6" data-testid="entropy-type-chart">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Entropy Type Distribution</h3>
          <p className="text-xs text-slate-500 mb-4">By output mode</p>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={TYPE_DATA} innerRadius={50} outerRadius={75} paddingAngle={2} dataKey="value">
                {TYPE_DATA.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
              </Pie>
              <Tooltip contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 0, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2 mt-2">
            {TYPE_DATA.map((t, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2"><div className="w-2.5 h-2.5" style={{ backgroundColor: t.color }} /><span className="text-slate-600">{t.name}</span></div>
                <span className="font-mono text-slate-900 font-medium">{t.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-white border border-slate-200 p-6" data-testid="latency-chart">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Latency Distribution</h3>
          <p className="text-xs text-slate-500 mb-4">Response time histogram</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={LATENCY_DATA}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="range" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 0, fontSize: 12 }} />
              <Bar dataKey="count" fill="#06B6D4" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 p-6" data-testid="bandwidth-chart">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Bandwidth Usage</h3>
          <p className="text-xs text-slate-500 mb-4">Entropy data transferred (GB)</p>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={BANDWIDTH_DATA}>
              <defs><linearGradient id="bwGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.15} /><stop offset="95%" stopColor="#10B981" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 0, fontSize: 12 }} />
              <Area type="monotone" dataKey="gb" stroke="#10B981" strokeWidth={2} fill="url(#bwGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Endpoints */}
      <div className="bg-white border border-slate-200" data-testid="top-endpoints-table">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900">Top Endpoints</h3>
        </div>
        <div>
          <div className="grid grid-cols-[1fr_100px_100px_110px_110px] bg-slate-50 border-b border-slate-200">
            {["Endpoint","Total Calls","Bandwidth","Avg Latency","P99 Latency"].map(h => (
              <div key={h} className="px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
            ))}
          </div>
          {TOP_ENDPOINTS.map((ep, i) => (
            <div key={i} className="grid grid-cols-[1fr_100px_100px_110px_110px] border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`endpoint-row-${i}`}>
              <div className="px-6 py-3 font-mono text-xs text-slate-900 font-medium flex items-center">{ep.endpoint}</div>
              <div className="px-6 py-3 font-mono text-xs text-slate-600 flex items-center">{ep.calls}</div>
              <div className="px-6 py-3 font-mono text-xs text-slate-600 flex items-center">{ep.bandwidth}</div>
              <div className="px-6 py-3 font-mono text-xs text-slate-600 flex items-center">{ep.avgLatency}</div>
              <div className="px-6 py-3 font-mono text-xs text-slate-600 flex items-center">{ep.p99}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
